/**
 * Optimized Social Proof Strip Component (Brands)
 * Displays company logos to build immediate credibility
 * Positioned right after hero section per UX conversion redesign
 *
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - Next.js Image for optimization
 * - Analytics tracking for visibility
 */

'use client';

import { memo, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

type BrandsProps = {
    brandsTitle: string;
    brands: { src: string; alt: string; name?: string }[];
    className?: string;
};

function BrandsListComponent({ brandsTitle, brands, className = "" }: BrandsProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.5,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('social_proof_strip_view', {
                total_brands: brands.length,
                section_title: brandsTitle,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, brands.length, brandsTitle, trackEvent]);

    if (!brands || brands.length === 0) return null;

    return (
        <div
            ref={sectionRef}
            className={`section_brands bg-white border-y border-gray-200 my-4 ${className}`}
            aria-label="Companies our clients work at"
        >
            <div className="brands_component has-no-border max-w-7xl mx-auto">
                <div className="margin-bottom margin-small">
                    <div className="max-width-large align-center">
                        <p className="text-color-gray-50 dark:text-gray-300 text-align-center">
                            {brandsTitle}
                        </p>
                    </div>
                </div>
                <div className="brands_list-wrapper w-dyn-list">
                    <div
                        role="list"
                        className="brands_list w-dyn-items auto-scroll flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                    >
                        {/* First Loop */}
                        {brands.map((brand, idx) => (
                            <div
                                role="listitem"
                                className="brands_list-item w-dyn-item w-dyn-repeater-item flex items-center justify-center"
                                key={`brand-first-${idx}`}
                            >
                                <Image
                                    src={brand.src}
                                    alt={brand.alt}
                                    width={120}
                                    height={32}
                                    className="brands_logo h-8 w-auto object-contain"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                        {/* Duplicate Loop for scroll effect */}
                        {brands.map((brand, idx) => (
                            <div
                                role="listitem"
                                className="brands_list-item w-dyn-item w-dyn-repeater-item flex items-center justify-center"
                                key={`brand-second-${idx}`}
                                aria-hidden="true"
                            >
                                <Image
                                    src={brand.src}
                                    alt={brand.alt}
                                    width={120}
                                    height={32}
                                    className="brands_logo h-8 w-auto object-contain"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export memoized component
export const BrandsList = memo(BrandsListComponent);
BrandsList.displayName = 'BrandsList';

// Default export for backward compatibility
export default BrandsList;
